import { supabase } from "./supabase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Déduit la date de l'événement (event_date) à partir du texte, via le backend (IA).
// Renvoie une chaîne ISO "YYYY-MM-DD" ou null. Tolérant aux erreurs.
async function extractEventDate(text) {
  const clean = (text || "").trim();
  if (!clean) return null;
  try {
    const res = await fetch(`${API}/extract-date`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: clean }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.event_date || null;
  } catch (e) {
    return null;
  }
}

// createMemory : si audio -> upload dans le bucket (préfixé user_id) puis insertion.
export async function createMemory({ transcript, audioBlob } = {}) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Pas de session active");

  let audio_path = null;
  if (audioBlob) {
    const ext = blobExtension(audioBlob);
    const path = `${user.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("audio")
      .upload(path, audioBlob, {
        contentType: audioBlob.type || "audio/webm",
        upsert: false,
      });
    if (upErr) throw upErr;
    audio_path = path;
  }

  // Pré-remplit event_date côté backend (IA) pour le classement par année.
  const event_date = await extractEventDate(transcript);

  const insertPayload = { transcript: transcript?.trim() || null, audio_path };
  if (event_date) insertPayload.event_date = event_date;

  const { data, error } = await supabase
    .from("memories")
    .insert(insertPayload)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function listMemories() {
  const { data, error } = await supabase
    .from("memories")
    .select("id, transcript, audio_path, event_date, created_at, metadata")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

// Édition d'un souvenir : texte et/ou tags (stockés dans metadata.tags).
export async function updateMemory(id, { transcript, tags, metadata } = {}) {
  const newMeta = { ...(metadata || {}), tags: Array.isArray(tags) ? tags : [] };
  const { data, error } = await supabase
    .from("memories")
    .update({ transcript: transcript?.trim() || null, metadata: newMeta })
    .eq("id", id)
    .select("id, transcript, audio_path, event_date, created_at, metadata")
    .single();
  if (error) throw error;
  return data;
}

// Met à jour uniquement la date du souvenir (event_date), saisie manuellement.
export async function updateMemoryEventDate(id, eventDate) {
  const { data, error } = await supabase
    .from("memories")
    .update({ event_date: eventDate })
    .eq("id", id)
    .select("id, transcript, audio_path, event_date, created_at, metadata")
    .single();
  if (error) throw error;
  return data;
}

// Met à jour uniquement des clés de metadata (ex. images), en préservant le reste.
export async function patchMemoryMetadata(id, currentMetadata, patch) {
  const newMeta = { ...(currentMetadata || {}), ...patch };
  const { data, error } = await supabase
    .from("memories")
    .update({ metadata: newMeta })
    .eq("id", id)
    .select("id, transcript, audio_path, event_date, created_at, metadata")
    .single();
  if (error) throw error;
  return data;
}

// Tous les souvenirs (export complet).
export async function getAllMemories() {
  const { data, error } = await supabase
    .from("memories")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

// Supprime tous les souvenirs de l'utilisateur (RGPD) + fichiers audio associés.
export async function deleteAllMemories() {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Pas de session active");

  // Suppression des audios (best effort).
  try {
    const { data: files } = await supabase.storage.from("audio").list(user.id);
    if (files && files.length) {
      await supabase.storage
        .from("audio")
        .remove(files.map((f) => `${user.id}/${f.name}`));
    }
  } catch (e) {
    /* no-op */
  }

  const { error } = await supabase.from("memories").delete().eq("user_id", user.id);
  if (error) throw error;
}

// Supprime un souvenir : la ligne (RLS = la sienne) ET son fichier audio.
export async function deleteMemory(id) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Pas de session active");

  // Récupère l'audio_path pour effacer aussi le fichier du bucket.
  const { data: row } = await supabase
    .from("memories")
    .select("audio_path")
    .eq("id", id)
    .single();

  if (row && row.audio_path) {
    try {
      await supabase.storage.from("audio").remove([row.audio_path]);
    } catch (e) {
      /* no-op */
    }
  }

  const { error } = await supabase.from("memories").delete().eq("id", id);
  if (error) throw error;
}

// URL signée temporaire pour réécouter un audio du bucket privé.
export async function getAudioUrl(path) {
  const { data, error } = await supabase.storage
    .from("audio")
    .createSignedUrl(path, 3600);
  if (error) throw error;
  return data.signedUrl;
}

function blobExtension(blob) {
  const type = blob.type || "audio/webm";
  if (type.includes("mp4")) return "mp4";
  if (type.includes("mpeg") || type.includes("mp3")) return "mp3";
  if (type.includes("wav")) return "wav";
  if (type.includes("ogg")) return "ogg";
  return "webm";
}
